import SunCalculator from '../SunCalculator';
import { getCache, setCache } from './cacheUtils';

// Calculate Golden Hour (typically 1 hour before sunset)
const calcGoldenHour = (sunset) => {
  if (!sunset) return null;
  
  try {
    const start = new Date(sunset.getTime() - (60 * 60 * 1000)); // 1 hour before sunset
    return {
      start: start,
      end: sunset
    };
  } catch (error) {
    console.error('Error calculating golden hour:', error);
    return null;
  }
};

// Calculate Blue Hour (typically after sunset)
const calcBlueHour = (civilTwilightEnd) => {
  if (!civilTwilightEnd) return null;
  
  try {
    const start = new Date(civilTwilightEnd.getTime() - (20 * 60 * 1000)); // 20 minutes before civil twilight end
    const end = new Date(civilTwilightEnd.getTime() + (20 * 60 * 1000)); // 20 minutes after civil twilight end
    return {
      start: start,
      end: end
    };
  } catch (error) {
    console.error('Error calculating blue hour:', error);
    return null;
  }
};

/**
 * Fetch sunrise/sunset data from external API
 * @param {number} lat - Latitude coordinate
 * @param {number} lng - Longitude coordinate
 * @param {Date} date - Date to fetch data for
 * @returns {Promise<Object>} Sunrise/sunset data
 */
export const fetchSunriseSunsetData = async (lat, lng, date = new Date()) => {
  // Format date for API
  const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
  
  // Create cache key
  const cacheKey = `sunriseApp.api.${lat.toFixed(4)},${lng.toFixed(4)}.${dateStr}`;
  
  // Check cache first
  const cachedData = getCache(cacheKey);
  if (cachedData) {
    
    // Convert date strings back to Date objects
    const result = { ...cachedData };
    if (result.sunrise) result.sunrise = new Date(result.sunrise);
    if (result.sunset) result.sunset = new Date(result.sunset);
    if (result.civil_twilight_begin) result.civil_twilight_begin = new Date(result.civil_twilight_begin);
    if (result.civil_twilight_end) result.civil_twilight_end = new Date(result.civil_twilight_end);
    if (result.nautical_twilight_begin) result.nautical_twilight_begin = new Date(result.nautical_twilight_begin);
    if (result.nautical_twilight_end) result.nautical_twilight_end = new Date(result.nautical_twilight_end);
    if (result.astronomical_twilight_begin) result.astronomical_twilight_begin = new Date(result.astronomical_twilight_begin);
    if (result.astronomical_twilight_end) result.astronomical_twilight_end = new Date(result.astronomical_twilight_end);
    if (result.solar_noon) result.solar_noon = new Date(result.solar_noon);
    
    // Recalculate golden hour and blue hour with the Date objects
    if (result.sunset) {
      result.golden_hour = calcGoldenHour(result.sunset);
    }
    
    if (result.civil_twilight_end) {
      result.blue_hour = calcBlueHour(result.civil_twilight_end);
    }
    
    return result;
  }
  
  try {
    // Real API call to sunrise-sunset.org API
    const response = await fetch(`https://api.sunrise-sunset.org/json?lat=${lat}&lng=${lng}&date=${dateStr}&formatted=0`);
    
    if (!response.ok) {
      throw new Error(`API responded with status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.status === 'OK') {
      // Validate parsed dates
      const validateDate = (d, name) => {
        if (!(d instanceof Date) || isNaN(d.getTime())) {
          throw new Error(`Invalid date for ${name}`);
        }
        return d;
      };

      // Parse and validate the data
      const sunrise = validateDate(new Date(data.results.sunrise), 'sunrise');
      const sunset = validateDate(new Date(data.results.sunset), 'sunset');
      const civil_twilight_begin = new Date(data.results.civil_twilight_begin);
      const civil_twilight_end = new Date(data.results.civil_twilight_end);
      const nautical_twilight_begin = new Date(data.results.nautical_twilight_begin);
      const nautical_twilight_end = new Date(data.results.nautical_twilight_end);
      const astronomical_twilight_begin = new Date(data.results.astronomical_twilight_begin);
      const astronomical_twilight_end = new Date(data.results.astronomical_twilight_end);
      const solar_noon = new Date(data.results.solar_noon);
      
      // Calculate derived data
      const golden_hour = calcGoldenHour(sunset);
      const blue_hour = calcBlueHour(civil_twilight_end);
      
      const result = {
        sunrise,
        sunset,
        civil_twilight_begin,
        civil_twilight_end,
        nautical_twilight_begin,
        nautical_twilight_end,
        astronomical_twilight_begin,
        astronomical_twilight_end,
        day_length: data.results.day_length,
        solar_noon,
        golden_hour,
        blue_hour,
        polarNight: false,
        polarDay: false
      };
      
      // Cache the result
      setCache(cacheKey, result);
      
      return result;
    } else {
      throw new Error('API response was not OK');
    }
  } catch (error) {
    console.error('Error fetching sunrise/sunset data, falling back to calculation:', error.message);

    // Try stale cache before falling back to calculation
    const staleData = getCache(cacheKey, true);
    if (staleData) {
      const result = { ...staleData };
      if (result.sunrise) result.sunrise = new Date(result.sunrise);
      if (result.sunset) result.sunset = new Date(result.sunset);
      if (result.civil_twilight_begin) result.civil_twilight_begin = new Date(result.civil_twilight_begin);
      if (result.civil_twilight_end) result.civil_twilight_end = new Date(result.civil_twilight_end);
      if (result.nautical_twilight_begin) result.nautical_twilight_begin = new Date(result.nautical_twilight_begin);
      if (result.nautical_twilight_end) result.nautical_twilight_end = new Date(result.nautical_twilight_end);
      if (result.astronomical_twilight_begin) result.astronomical_twilight_begin = new Date(result.astronomical_twilight_begin);
      if (result.astronomical_twilight_end) result.astronomical_twilight_end = new Date(result.astronomical_twilight_end);
      if (result.solar_noon) result.solar_noon = new Date(result.solar_noon);
      if (result.sunset) result.golden_hour = calcGoldenHour(result.sunset);
      if (result.civil_twilight_end) result.blue_hour = calcBlueHour(result.civil_twilight_end);
      return result;
    }

    // Fallback to calculation if API fails and no stale cache
    const calculatedTimes = SunCalculator.calculateTimes(date, lat, lng);
    
    // Add additional data that would have come from the API
    calculatedTimes.civil_twilight_begin = new Date(calculatedTimes.sunrise.getTime() - 30 * 60 * 1000);
    calculatedTimes.civil_twilight_end = new Date(calculatedTimes.sunset.getTime() + 30 * 60 * 1000);
    calculatedTimes.nautical_twilight_begin = new Date(calculatedTimes.sunrise.getTime() - 60 * 60 * 1000);
    calculatedTimes.nautical_twilight_end = new Date(calculatedTimes.sunset.getTime() + 60 * 60 * 1000);
    calculatedTimes.astronomical_twilight_begin = new Date(calculatedTimes.sunrise.getTime() - 90 * 60 * 1000);
    calculatedTimes.astronomical_twilight_end = new Date(calculatedTimes.sunset.getTime() + 90 * 60 * 1000);
    calculatedTimes.day_length = (calculatedTimes.sunset - calculatedTimes.sunrise) / 1000;
    
    // Solar noon ≈ midpoint between sunrise and sunset
    calculatedTimes.solar_noon = new Date(
      (calculatedTimes.sunrise.getTime() + calculatedTimes.sunset.getTime()) / 2
    );
    
    calculatedTimes.golden_hour = calcGoldenHour(calculatedTimes.sunset);
    calculatedTimes.blue_hour = calcBlueHour(calculatedTimes.civil_twilight_end);
    
    // Cache the fallback result
    setCache(cacheKey, calculatedTimes);
    
    return calculatedTimes;
  }
};

/**
 * Calculate and return comprehensive sun times information
 * @param {number} lat - Latitude coordinate
 * @param {number} lng - Longitude coordinate
 * @param {Date} locationTime - The current time at the specified location
 * @returns {Promise<Object>} Comprehensive sun times data
 */
export const calculateSunTimes = async (lat, lng, locationTime = new Date()) => {
  const today = new Date(locationTime);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // IMPORTANT: Use the real UTC clock for all timestamp comparisons against
  // API dates. `locationTime` is a synthetic Date whose getHours() returns
  // the location's local hours, but its internal timestamp is NOT real UTC.
  // API dates (sunrise/sunset) ARE real UTC timestamps, so comparisons
  // must use a real Date to avoid wrong isDay / progress when the machine
  // timezone differs from the viewed location.
  const now = new Date();

  try {
    // Get yesterday's, today's and tomorrow's sunrise/sunset times
    const yesterdayTimes = await fetchSunriseSunsetData(lat, lng, yesterday);
    const todayTimes = await fetchSunriseSunsetData(lat, lng, today);
    const tomorrowTimes = await fetchSunriseSunsetData(lat, lng, tomorrow);

    // Handle special cases
    if (todayTimes.polarDay) {
      return {
        sunrise: null,
        sunset: null,
        nextEvent: null,
        nextTime: null,
        isDay: true,
        progress: 0.5,
        specialCase: 'polarDay',
        additionalInfo: todayTimes
      };
    } else if (todayTimes.polarNight) {
      return {
        sunrise: null,
        sunset: null,
        nextEvent: null,
        nextTime: null,
        isDay: false,
        progress: 0.5,
        specialCase: 'polarNight',
        additionalInfo: todayTimes
      };
    }

    // Determine if it's day or night using real UTC timestamps
    let isDay = false;

    if (todayTimes.sunrise && todayTimes.sunset) {
      isDay = now >= todayTimes.sunrise && now < todayTimes.sunset;
    } else {
      // Fallback using location's local hour
      const currentHour = today.getHours();
      isDay = currentHour >= 7 && currentHour < 19;
    }

    // Determine next event and time
    let nextEvent, nextTime;

    if (isDay) {
      nextEvent = 'sunset';
      nextTime = now >= todayTimes.sunset ? tomorrowTimes.sunset : todayTimes.sunset;
    } else {
      // Night - next event is sunrise
      nextEvent = 'sunrise';
      if (now < todayTimes.sunrise) {
        // Before today's sunrise
        nextTime = todayTimes.sunrise;
      } else {
        // After today's sunset
        nextTime = tomorrowTimes.sunrise;
      }
    }

    // Calculate progress for the dial using real UTC timestamps
    let progress = 0;

    if (isDay) {
      // Day progress: from sunrise to sunset
      const dayLength = todayTimes.sunset - todayTimes.sunrise;
      if (dayLength <= 0) {
        progress = 0.5;
      } else {
        const elapsed = now - todayTimes.sunrise;
        progress = Math.max(0, Math.min(1, elapsed / dayLength));
      }
    } else {
      // Night progress: from sunset to next sunrise
      let nightStart, nightEnd;

      if (now < todayTimes.sunrise) {
        // It's between midnight and sunrise
        nightStart = yesterdayTimes.sunset;
        nightEnd = todayTimes.sunrise;
      } else {
        // It's between sunset and midnight
        nightStart = todayTimes.sunset;
        nightEnd = tomorrowTimes.sunrise;
      }

      if (!nightStart || !nightEnd || nightEnd <= nightStart) {
        progress = 0.5;
      } else {
        const nightLength = nightEnd - nightStart;
        const elapsed = now - nightStart;
        progress = Math.max(0, Math.min(1, elapsed / nightLength));
      }
    }
    
    // Prepare additional info
    let additionalInfo = null;
    
    try {
      additionalInfo = {
        civilTwilight: {
          begin: todayTimes.civil_twilight_begin,
          end: todayTimes.civil_twilight_end
        },
        nauticalTwilight: {
          begin: todayTimes.nautical_twilight_begin,
          end: todayTimes.nautical_twilight_end
        },
        astronomicalTwilight: {
          begin: todayTimes.astronomical_twilight_begin,
          end: todayTimes.astronomical_twilight_end
        },
        goldenHour: todayTimes.golden_hour,
        blueHour: todayTimes.blue_hour,
        solarNoon: todayTimes.solar_noon,
        dayLength: todayTimes.day_length
      };
    } catch (error) {
      console.error('Error preparing additional info:', error);
    }
    
    return {
      sunrise: todayTimes.sunrise,
      sunset: todayTimes.sunset,
      nextEvent,
      nextTime,
      isDay,
      progress,
      specialCase: null,
      // Include additional information
      additionalInfo
    };
  } catch (error) {
    console.error('Error in calculateSunTimes:', error);
    // Fallback to simple calculation — use location hours for rough estimate
    const fallbackTimes = SunCalculator.calculateTimes(today, lat, lng);
    const locHour = today.getHours();
    const fallbackIsDay = (locHour >= 6 && locHour < 18);

    return {
      sunrise: fallbackTimes.sunrise,
      sunset: fallbackTimes.sunset,
      nextEvent: fallbackIsDay ? 'sunset' : 'sunrise',
      nextTime: fallbackIsDay ? fallbackTimes.sunset : fallbackTimes.sunrise,
      isDay: fallbackIsDay,
      progress: fallbackIsDay
        ? (locHour - 6) / 12
        : (locHour < 6 ? (locHour + 24 - 18) / 12 : (locHour - 18) / 12),
      specialCase: null,
      additionalInfo: null
    };
  }
};